import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DashboardReport } from '../../types';
import apiService from '../../services/api';

interface DashboardState {
  report: DashboardReport | null;
  period: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all';
  loading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  report: null,
  period: 'daily',
  loading: false,
  error: null,
};

// Map period from UI format to API format
const mapPeriodToApi = (period: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all'): 'day' | 'week' | 'month' | 'all' => {
  const mapping: Record<string, 'day' | 'week' | 'month' | 'all'> = {
    'daily': 'day',
    'weekly': 'week',
    'monthly': 'month',
    'yearly': 'month', // API doesn't have yearly, use month
    'all': 'all',
  };
  return mapping[period] || 'all';
};

export const fetchDashboardReport = createAsyncThunk(
  'dashboard/fetchDashboardReport',
  async (period: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all' = 'all') => {
    const apiPeriod = mapPeriodToApi(period);
    const response = await apiService.getDashboardReport(apiPeriod);
    return response;
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPeriod: (state, action: PayloadAction<'daily' | 'weekly' | 'monthly' | 'yearly' | 'all'>) => {
      state.period = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardReport.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.report = action.payload;
        } else {
          state.error = 'Failed to fetch dashboard report';
        }
      })
      .addCase(fetchDashboardReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard report';
      });
  },
});

export const { clearError, setPeriod } = dashboardSlice.actions;
export default dashboardSlice.reducer; 